import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ChevronLeft, ChevronRight, Users } from 'lucide-react';

/**
 * Registration campaigns on the dashboard: one block per link group with how
 * many registered, how many still wait to be verified, and the bundle's answer
 * split — the gender row a manager otherwise has to open the campaign to see.
 *
 * Each question is a single 100% stacked bar with a labelled legend under it:
 * the question here is "how does this campaign divide", so proportion is the
 * job and one bar answers it at a glance. Identity is never carried by color
 * alone — every segment is named in the legend with its own count, so the card
 * still reads in grayscale, under CVD, and for a screen reader.
 *
 * Palette: the data-viz reference categorical slots, validated against both the
 * light (#fff) and dark (#1E293B) app surfaces — the dark steps are their own
 * values, not a flipped copy. Light mode's mid-tone slots sit under 3:1 against
 * white, which is why the labels are mandatory, not decorative.
 *
 * Data: GET /course-registration/link-groups-summary.
 */

const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7'];
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#9085e9'];

// Beyond the palette's fixed slots a hue is never generated — the remaining
// options fold into one neutral "other" segment, still named in the legend.
const OTHER = 'var(--color-text-tertiary, #94a3b8)';

function seriesColor(index, dark) {
  const slots = dark ? SERIES_DARK : SERIES_LIGHT;
  return index < slots.length ? slots[index] : OTHER;
}

/** Fold everything past the palette's slots into one "other" bucket. */
export function segmentsFor(question, maxSlots = SERIES_LIGHT.length) {
  const options = (question.options || []).filter((o) => o.count > 0);
  if (options.length <= maxSlots) return options.map((o, i) => ({ ...o, slot: i }));
  const head = options.slice(0, maxSlots - 1).map((o, i) => ({ ...o, slot: i }));
  const rest = options.slice(maxSlots - 1);
  const count = rest.reduce((n, o) => n + o.count, 0);
  return [...head, {
    value: null,
    otherCount: rest.length,
    count,
    percentage: question.answered ? Math.round((count / question.answered) * 1000) / 10 : 0,
    slot: maxSlots - 1,
    isOther: true,
  }];
}

function QuestionBar({ question, dark }) {
  const { t } = useTranslation();
  const segments = segmentsFor(question);
  if (segments.length === 0) return null;
  const total = segments.reduce((n, s) => n + s.count, 0) || 1;

  return (
    <div style={S.question}>
      <div style={S.questionLabel}>{question.label}</div>
      {/* One bar, segments separated by a 2px surface gap so adjacent fills
          never blend into a single block. */}
      <div style={S.bar} role="img" aria-label={`${question.label}: ${segments.map((s) => `${s.isOther ? t('dashboard.registrationSummary.otherAnswers', 'other') : s.value} ${s.count}`).join(', ')}`}>
        {segments.map((s, i) => (
          <span
            key={s.value || `other_${i}`}
            style={{
              ...S.segment,
              width: `${(s.count / total) * 100}%`,
              background: seriesColor(s.slot, dark),
              marginInlineEnd: i === segments.length - 1 ? 0 : 2,
            }}
          />
        ))}
      </div>
      <div style={S.legend}>
        {segments.map((s, i) => (
          <span key={s.value || `other_${i}`} style={S.legendItem}>
            <span style={{ ...S.dot, background: seriesColor(s.slot, dark) }} />
            <span style={S.legendName}>
              {s.isOther
                ? t('dashboard.registrationSummary.otherCount', '{{n}} more', { n: s.otherCount })
                : s.value}
            </span>
            <b style={S.legendValue}>{s.count}</b>
            <span style={S.legendPct}>{s.percentage}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RegistrationSummaryCard({ groups = [], isLoading = false, maxGroups = 3 }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rtl = i18n.language === 'ar' || i18n.language === 'he';
  const Chevron = rtl ? ChevronLeft : ChevronRight;
  // The card reads its own colors off the document theme rather than a prop,
  // so it stays correct when the user flips the theme without a remount.
  const dark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

  // A campaign with no registrations yet is still worth showing (it says the
  // link is live and nobody has come); a center with no campaigns at all is
  // not — the card would be an empty frame on every dashboard.
  if (isLoading || groups.length === 0) return null;
  const shown = groups.slice(0, maxGroups);

  return (
    <section style={S.card} data-testid="registration-summary-card">
      <header style={S.head}>
        <h3 style={S.title}><ClipboardList size={18} /> {t('dashboard.registrationSummary.title', 'Registration links')}</h3>
        <button type="button" onClick={() => navigate('/registration')} style={S.allBtn}>
          {t('dashboard.registrationSummary.viewAll', 'All links')} <Chevron size={15} />
        </button>
      </header>

      <div style={S.groups}>
        {shown.map((g) => (
          <div
            key={g.id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/registration/group/${g.id}/analytics`)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/registration/group/${g.id}/analytics`); } }}
            style={S.group}
          >
            <div style={S.groupHead}>
              <span style={S.groupTitle}>{g.title}</span>
              <span style={S.registrants}><Users size={14} /> {g.registrantCount || 0}</span>
              {g.pendingCount > 0 && (
                <span style={S.pending}>{t('dashboard.registrationSummary.pending', '{{n}} to verify', { n: g.pendingCount })}</span>
              )}
            </div>
            {(g.questions || []).filter((q) => q.type === 'choice').map((q) => (
              <QuestionBar key={q.label} question={q} dark={dark} />
            ))}
          </div>
        ))}
      </div>

      {groups.length > shown.length && (
        <button type="button" onClick={() => navigate('/registration')} style={S.moreBtn}>
          {t('dashboard.registrationSummary.more', '{{n}} more links', { n: groups.length - shown.length })}
        </button>
      )}
    </section>
  );
}

const S = {
  card: { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 12, padding: 16 },
  head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  title: { display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--color-text-primary, #1A1F36)' },
  allBtn: { marginInlineStart: 'auto', display: 'inline-flex', alignItems: 'center', gap: 2, background: 'none', border: 0, padding: 0, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: 'var(--color-primary-dark, #355760)' },
  groups: { display: 'grid', gap: 12 },
  group: { padding: 12, borderRadius: 10, border: '1px solid var(--color-border, #eef2f5)', background: 'var(--color-surface-alt, #f8fafc)', cursor: 'pointer' },
  groupHead: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  groupTitle: { fontWeight: 700, fontSize: '0.92rem', color: 'var(--color-text-primary, #1A1F36)' },
  registrants: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-text-secondary, #6b7280)' },
  pending: { padding: '1px 8px', borderRadius: 9999, background: 'rgba(245,158,11,0.16)', color: 'var(--color-warning-dark, #b45309)', fontSize: '0.72rem', fontWeight: 700 },
  question: { marginBlockStart: 10 },
  questionLabel: { fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-secondary, #6b7280)', marginBlockEnd: 5 },
  bar: { display: 'flex', width: '100%', height: 10, borderRadius: 5, overflow: 'hidden' },
  segment: { display: 'block', height: '100%', borderRadius: 4 },
  legend: { display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBlockStart: 7 },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.78rem' },
  dot: { width: 8, height: 8, borderRadius: 9999, flexShrink: 0 },
  legendName: { color: 'var(--color-text-secondary, #6b7280)' },
  legendValue: { color: 'var(--color-text-primary, #1A1F36)', fontVariantNumeric: 'tabular-nums' },
  legendPct: { color: 'var(--color-text-tertiary, #94a3b8)', fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' },
  moreBtn: { marginBlockStart: 10, width: '100%', padding: '8px 0', borderRadius: 9, border: '1px dashed var(--color-border, #e5e7eb)', background: 'none', color: 'var(--color-text-secondary, #6b7280)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' },
};
